import { requireCondition } from './errors.js';

export function normalizeWord(word, languageCode) {
  const normalized = word.normalize('NFC').trim().toLocaleLowerCase(languageCode).normalize('NFC');
  if (languageCode === 'fr') return normalized.replaceAll('œ', 'oe').replaceAll('æ', 'ae').normalize('NFD').replace(/\p{M}/gu, '').normalize('NFC');
  return languageCode === 'es' ? normalized.replace(/[áéíóúü]/gu, letter => ({ á: 'a', é: 'e', í: 'i', ó: 'o', ú: 'u', ü: 'u' })[letter]) : normalized;
}

export function validateLanguagePack(input) {
  requireCondition(input && typeof input === 'object' && !Array.isArray(input), 'invalid_language_pack');
  requireCondition(typeof input.languageCode === 'string', 'invalid_language_pack', 422, 'A language code is required.');
  let languageCode;
  try { languageCode = new Intl.Locale(input.languageCode).toString(); }
  catch { requireCondition(false, 'invalid_language_pack', 422, 'Invalid language code.'); }
  requireCondition(languageCode === input.languageCode && languageCode.length <= 35, 'invalid_language_pack', 422, 'Use a canonical language code.');
  requireCondition(typeof input.name === 'string' && input.name.trim().length > 0 && input.name.length <= 80, 'invalid_language_pack', 422, 'A language name is required.');
  requireCondition(['ltr', 'rtl'].includes(input.direction), 'invalid_language_pack', 422, 'Direction must be ltr or rtl.');
  requireCondition(Array.isArray(input.letters) && input.letters.length >= 1 && input.letters.length <= 256, 'invalid_language_pack', 422, 'Provide 1–256 letters.');
  requireCondition(Array.isArray(input.groups) && input.groups.length >= 1 && input.groups.length <= 16, 'invalid_language_pack', 422, 'Provide 1–16 generation groups.');
  const groupIds = new Set();
  const groups = input.groups.map(group => {
    requireCondition(group && typeof group.id === 'string' && /^[a-z][a-z0-9_-]{0,31}$/.test(group.id) && !groupIds.has(group.id), 'invalid_language_pack', 422, 'Group IDs must be unique.');
    requireCondition(Number.isInteger(group.minimum) && Number.isInteger(group.maximum) && group.minimum >= 0 && group.maximum >= group.minimum && group.maximum <= 24, 'invalid_language_pack', 422, 'Invalid group counts.');
    groupIds.add(group.id);
    return { id: group.id, minimum: group.minimum, maximum: group.maximum };
  });
  const minimumTileCount = groups.reduce((total, group) => total + group.minimum, 0);
  const maximumTileCount = groups.reduce((total, group) => total + group.maximum, 0);
  requireCondition(minimumTileCount >= 1 && maximumTileCount <= 24, 'invalid_language_pack', 422, 'Boards must contain 1–24 tiles.');
  requireCondition(Number.isInteger(input.minimumWordLength) && Number.isInteger(input.maximumWordLength) && input.minimumWordLength >= 1 && input.maximumWordLength >= input.minimumWordLength && input.maximumWordLength <= maximumTileCount, 'invalid_language_pack', 422, 'Invalid word length range.');
  const letterIds = new Set();
  const symbols = new Set();
  const letters = input.letters.map((letter, index) => {
    requireCondition(letter && typeof letter.id === 'string' && /^[a-z0-9_-]{1,40}$/.test(letter.id) && !letterIds.has(letter.id), 'invalid_language_pack', 422, 'Letter IDs must be unique.');
    requireCondition(typeof letter.symbol === 'string' && /^[\p{L}\p{M}]+$/u.test(letter.symbol) && /\p{L}/u.test(letter.symbol) && [...letter.symbol].length <= 12, 'invalid_language_pack', 422, 'A tile must contain letters, with a maximum of 12 code points.');
    const symbol = normalizeWord(letter.symbol, languageCode);
    requireCondition(!symbols.has(symbol), 'invalid_language_pack', 422, 'Normalized symbols must be unique.');
    requireCondition(Number.isInteger(letter.points) && letter.points >= 0 && letter.points <= 1000, 'invalid_language_pack', 422, 'Points must be integers from 0 to 1000.');
    requireCondition(Number.isFinite(letter.weight) && letter.weight > 0 && letter.weight <= 1000000, 'invalid_language_pack', 422, 'Weights must be positive numbers.');
    requireCondition(groupIds.has(letter.group) && typeof letter.enabled === 'boolean', 'invalid_language_pack', 422, 'Invalid letter group or enabled flag.');
    letterIds.add(letter.id);
    symbols.add(symbol);
    return { id: letter.id, symbol, points: letter.points, group: letter.group, weight: letter.weight, enabled: letter.enabled, sortOrder: index };
  });
  for (const group of groups) {
    requireCondition(group.maximum === 0 || letters.some(letter => letter.enabled && letter.group === group.id), 'invalid_language_pack', 422, `Group ${group.id} has no enabled letters.`);
  }
  return { languageCode, name: input.name.trim(), direction: input.direction, minimumWordLength: input.minimumWordLength, maximumWordLength: input.maximumWordLength, groups, letters };
}

/** Longest matching configured symbol wins; shared by imports and game scoring. */
export function tokenizeWord(normalizedWord, languagePack, includeDisabled = false) {
  return createWordTokenizer(languagePack, includeDisabled)(normalizedWord);
}

export function createWordTokenizer(languagePack, includeDisabled = false) {
  const symbols = languagePack.letters.filter(letter => includeDisabled || letter.enabled)
    .map(letter => letter.symbol).sort((first, second) => second.length - first.length);
  return normalizedWord => {
    const tokens = [];
    let characterOffset = 0;
    while (characterOffset < normalizedWord.length) {
      const symbol = symbols.find(candidate => normalizedWord.startsWith(candidate, characterOffset));
      if (!symbol) return null;
      tokens.push(symbol);
      characterOffset += symbol.length;
    }
    return tokens;
  };
}
